import { useMemo, useState } from 'react';
import {
  App,
  Button,
  Popover,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  HistoryOutlined,
  InboxOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import { useMockStore } from '@/mocks/store';
import type { DocumentRecord } from '@/mocks/types';
import { format } from '@/theme/format';
import { semantic } from '@/theme/tokens';
import { useReadOnly } from '@/hooks/useReadOnly';
import EmptyState from './EmptyState';

const { Text } = Typography;
const { Dragger } = Upload;

/**
 * Tagged-union scope policy (FR-160..163) — Sprint 2 Phase B final.
 *
 * Four kinds, no more. When a detail page sits below a parent entity, scope
 * to the parent:
 *   kind:'project'  — Projects, Submittals, Drawings, MR, TechDocs, PO, RFQ,
 *                     RFP, Close-Out, O&M Manuals, Warranty.
 *   kind:'contract' — Client/Sub contracts, IPC, VO, Claims, Correspondence.
 *                     POs scope to the project, NOT the contract.
 *   kind:'employee' — HR Employee detail.
 *   kind:'generic'  — Ad-hoc / pre-contract cases. Caller supplies docs.
 */
export interface DocumentAttachmentsPanelProps {
  scope:
    | { kind: 'project'; id: string }
    | { kind: 'contract'; id: string }
    | { kind: 'employee'; id: string }
    | { kind: 'generic'; docs: DocumentRecord[] };
  /** Allow upload (creates an in-memory document record — OOS-004). */
  allowUpload?: boolean;
  /** Dense layout for wizards. */
  dense?: boolean;
}

/**
 * S4-T14 — First-class Documents tab with version-history depth.
 *
 * Features:
 *   - Drag-drop dropzone (no real upload — OOS-004 in-memory only)
 *   - Per-row version-history popover walking the supersedesId chain
 *   - "Upload new version" per-row action: marks the current as superseded
 *     and pushes a new record with supersedesId + bumped version label
 *   - "Show superseded" toggle (default off) so the live-state view stays clean
 *   - Preview + Download stubs (no real file bytes)
 */
export default function DocumentAttachmentsPanel({
  scope,
  allowUpload = true,
  dense,
}: DocumentAttachmentsPanelProps) {
  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const documents = useMockStore((s) => s.documents);
  const patch = useMockStore((s) => s.patch);
  const [nextId, setNextId] = useState(1);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const readOnly = useReadOnly();
  const canMutate = allowUpload && !readOnly;

  const scopedDocs = useMemo(() => filterDocs(scope, documents), [scope, documents]);

  const visibleRows = useMemo(
    () => (showSuperseded ? scopedDocs : scopedDocs.filter((d) => !d.superseded)),
    [scopedDocs, showSuperseded],
  );

  const supersededCount = scopedDocs.length - scopedDocs.filter((d) => !d.superseded).length;

  const onUpload: UploadProps['customRequest'] = (opts) => {
    const { file, onSuccess } = opts;
    const mockFile = file as File;
    const record: DocumentRecord = {
      id: `DOC-MOCK-${Date.now()}-${nextId}`,
      title: mockFile.name ?? `Mock document ${nextId}`,
      type: 'other',
      version: '1.0',
      superseded: false,
      uploadedBy: 'demo-user',
      uploadedAt: new Date().toISOString().slice(0, 10),
      sizeKb: Math.round((mockFile.size ?? 1024) / 1024) || 1,
      ...(scope.kind === 'project' && { projectId: scope.id }),
      ...(scope.kind === 'contract' && { contractId: scope.id }),
      ...(scope.kind === 'employee' && { employeeId: scope.id }),
    };
    patch({ documents: [...documents, record] });
    setNextId((n) => n + 1);
    onSuccess?.(null as never);
    void message.success(
      t('documents.uploadSuccess', { defaultValue: 'Attached (mock upload)' }),
    );
  };

  const onUploadNewVersion = (current: DocumentRecord) => {
    const newVersionLabel = bumpVersion(current.version);
    const newId = `DOC-MOCK-${Date.now()}-${nextId}`;
    const newRecord: DocumentRecord = {
      ...current,
      id: newId,
      version: newVersionLabel,
      superseded: false,
      supersedesId: current.id,
      uploadedBy: 'demo-user',
      uploadedAt: new Date().toISOString().slice(0, 10),
    };
    const next = documents.map((d) => (d.id === current.id ? { ...d, superseded: true } : d));
    patch({ documents: [...next, newRecord] });
    setNextId((n) => n + 1);
    void message.success(
      t('documents.newVersionToast', {
        defaultValue: 'New version {{version}} uploaded — previous marked superseded.',
        version: newVersionLabel,
      }),
    );
  };

  const onDelete = (id: string) => {
    patch({ documents: documents.filter((d) => d.id !== id) });
    void message.info(
      t('documents.deleteSuccess', { defaultValue: 'Removed from mock list' }),
    );
  };

  const buildVersionChain = (doc: DocumentRecord): DocumentRecord[] => {
    // Walk backwards via supersedesId, then forwards via reverse-link lookup.
    const back: DocumentRecord[] = [];
    let cursor: DocumentRecord | undefined = doc;
    while (cursor?.supersedesId) {
      const prev = documents.find((d) => d.id === cursor!.supersedesId);
      if (!prev) break;
      back.unshift(prev);
      cursor = prev;
    }
    const forward: DocumentRecord[] = [];
    let head: DocumentRecord | undefined = doc;
    while (head) {
      const next = documents.find((d) => d.supersedesId === head!.id);
      if (!next) break;
      forward.push(next);
      head = next;
    }
    return [...back, doc, ...forward];
  };

  const columns: ColumnsType<DocumentRecord> = [
    {
      title: t('documents.columns.title', { defaultValue: 'Title' }),
      dataIndex: 'title',
      key: 'title',
      render: (text: string, row) => (
        <Space size={6}>
          <PaperClipOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
          <span>{text}</span>
          {row.superseded && (
            <Tag color="default">
              {t('documents.superseded', { defaultValue: 'Superseded' })}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('documents.columns.type', { defaultValue: 'Type' }),
      dataIndex: 'type',
      key: 'type',
      width: 110,
    },
    {
      title: t('documents.columns.version', { defaultValue: 'Version' }),
      dataIndex: 'version',
      key: 'version',
      width: 160,
      render: (v: string, row) => {
        const chain = buildVersionChain(row);
        const hasChain = chain.length > 1;
        return (
          <Space size={4}>
            <span>{v}</span>
            {hasChain && (
              <Popover
                trigger="click"
                title={t('documents.versionHistory', { defaultValue: 'Version history' })}
                content={
                  <div style={{ maxWidth: 320 }}>
                    {chain.map((step, idx) => (
                      <div
                        key={step.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 0',
                          borderTop: idx > 0 ? '1px solid rgba(0,0,0,0.06)' : undefined,
                          opacity: step.superseded ? 0.6 : 1,
                          fontWeight: step.id === row.id ? 500 : 400,
                        }}
                      >
                        <Tag
                          color={step.superseded ? 'default' : 'green'}
                          style={{ fontSize: 10, marginInlineEnd: 0 }}
                        >
                          {step.version}
                        </Tag>
                        <span style={{ flex: 1, fontSize: 12 }}>
                          {step.uploadedBy} · {format.date(step.uploadedAt, 'short')}
                        </span>
                        {step.id === row.id && (
                          <Text
                            style={{ fontSize: 10, color: semantic.brand.primary }}
                          >
                            {t('documents.currentlyViewing', { defaultValue: 'this row' })}
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                }
              >
                <Tooltip
                  title={t('documents.versionHistoryHint', {
                    defaultValue: '{{count}} versions in chain',
                    count: chain.length,
                  })}
                >
                  <Button
                    size="small"
                    type="text"
                    icon={<HistoryOutlined />}
                    style={{ padding: 0, height: 18, color: semantic.brand.accent }}
                  />
                </Tooltip>
              </Popover>
            )}
          </Space>
        );
      },
    },
    {
      title: t('documents.columns.uploadedBy', { defaultValue: 'Uploaded by' }),
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 160,
    },
    {
      title: t('documents.columns.uploadedAt', { defaultValue: 'Date' }),
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 140,
      render: (iso: string) => format.date(iso, 'short'),
    },
    {
      title: t('documents.columns.size', { defaultValue: 'Size' }),
      dataIndex: 'sizeKb',
      key: 'sizeKb',
      width: 90,
      render: (kb: number) => `${kb} KB`,
    },
    {
      title: t('documents.columns.actions', { defaultValue: 'Actions' }),
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space size={2}>
          <Tooltip title={t('documents.preview', { defaultValue: 'Preview (stub)' })}>
            <Button size="small" type="text" icon={<EyeOutlined />} />
          </Tooltip>
          <Tooltip title={t('documents.download', { defaultValue: 'Download (stub)' })}>
            <Button size="small" type="text" icon={<DownloadOutlined />} />
          </Tooltip>
          {canMutate && !row.superseded && (
            <Tooltip
              title={t('documents.uploadNewVersion', { defaultValue: 'Upload new version' })}
            >
              <Button
                size="small"
                type="text"
                icon={<UploadOutlined />}
                onClick={() => onUploadNewVersion(row)}
              />
            </Tooltip>
          )}
          {canMutate && (
            <Popconfirm
              title={t('documents.deleteConfirm', { defaultValue: 'Remove this attachment?' })}
              onConfirm={() => onDelete(row.id)}
              okText={t('buttons.delete')}
              cancelText={t('buttons.cancel')}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {canMutate && (
        <Dragger
          customRequest={onUpload}
          showUploadList={false}
          multiple
          style={{
            marginBottom: 12,
            background: 'rgba(31, 77, 58, 0.03)',
            borderColor: 'rgba(31, 77, 58, 0.2)',
          }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
            <InboxOutlined style={{ color: semantic.brand.primary, fontSize: 32 }} />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 13, fontWeight: 500 }}>
            {t('documents.dropzoneTitle', {
              defaultValue: 'Drag files here or click to attach',
            })}
            <Tag color="gold" style={{ marginInlineStart: 8, fontSize: 10, lineHeight: '16px' }}>
              {t('prototype', { defaultValue: 'prototype' })}
            </Tag>
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 11 }}>
            {t('documents.uploadHint', {
              defaultValue: 'No bytes stored — OOS-004 mock only.',
            })}
          </p>
        </Dragger>
      )}

      {supersededCount > 0 && (
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch
            size="small"
            checked={showSuperseded}
            onChange={setShowSuperseded}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('documents.showSuperseded', {
              defaultValue: 'Show superseded ({{count}})',
              count: supersededCount,
            })}
          </Text>
        </div>
      )}

      {visibleRows.length === 0 ? (
        <EmptyState
          compact
          title={t('documents.empty.title', { defaultValue: 'No documents yet' })}
          description={t('documents.empty.description', {
            defaultValue: 'Attach contracts, drawings, or letters to this record.',
          })}
        />
      ) : (
        <Table<DocumentRecord>
          rowKey="id"
          size={dense ? 'small' : 'middle'}
          columns={columns}
          dataSource={visibleRows}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      )}
    </div>
  );
}

function filterDocs(
  scope: DocumentAttachmentsPanelProps['scope'],
  docs: DocumentRecord[],
): DocumentRecord[] {
  if (scope.kind === 'generic') return scope.docs;
  if (scope.kind === 'project') return docs.filter((d) => d.projectId === scope.id);
  if (scope.kind === 'contract') return docs.filter((d) => d.contractId === scope.id);
  return docs.filter((d) => d.employeeId === scope.id);
}

/**
 * Bumps a version label predictably:
 *   v1 → v2, v2 → v3, 1.0 → 1.1, 2.3 → 2.4
 * Falls back to appending '+1' for unrecognised formats.
 */
function bumpVersion(version: string): string {
  const vMatch = version.match(/^v(\d+)$/i);
  if (vMatch) return `v${parseInt(vMatch[1]!, 10) + 1}`;
  const dotMatch = version.match(/^(\d+)\.(\d+)$/);
  if (dotMatch) return `${dotMatch[1]}.${parseInt(dotMatch[2]!, 10) + 1}`;
  return `${version}+1`;
}
